import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { getDistance } from 'geolib'
import axios from 'axios'
import dayjs from 'dayjs'

type LocationStore = {
    geo: {
        data: {
            latitude: number
            longitude: number
        } | false
        message: string
    } | null
    weather: {
        data: any | false
        message: string
    } | null

    getGeoLocation: () => void
    getNearestWeather: (latitude: number, longitude: number) => void
}
const useLocation = create<LocationStore>()(
    persist(
        (set, get) => ({
            geo: null as LocationStore["geo"],
            weather: null as LocationStore["weather"],

            getGeoLocation: () => {
                if ("geolocation" in navigator) {
                    const success = (e: any) => {
                        // console.log(e.coords)
                        set({ geo: {
                            message: 'success',
                            data: {
                                latitude: e.coords.latitude,
                                longitude: e.coords.longitude
                            }
                        }})
                        get().getNearestWeather(e.coords.latitude, e.coords.longitude)
                    }
                    const error = (err: any) => {
                        // console.log('getLocation error: ', err)
                        const errorList: {[key: number]: string} = {
                            1: '使用者拒絕了定位請求',
                            2: '無法取得裝置位置 (GPS/網路故障等)',
                            3: '取得位置請求超時'
                        }
                        set({ geo: {
                            message: err.message || errorList[err.code],
                            data: false
                        }})
                    }
                    navigator.geolocation.getCurrentPosition(success, error)
                }
                else {
                    set({ geo: {
                        message: '裝置沒有定位功能',
                        data: false
                    }})
                }
            },
            getNearestWeather: (latitude: number, longitude: number) => {
                const auth = process.env.WEATHER_AUTHORIZATION

                if (!auth) {
                    set({ weather: {
                        message: '缺少 Authorization',
                        data: false
                    }})
                }
                else {
                    const urls = {
                        '台北市': 'https://opendata.cwa.gov.tw/api/v1/rest/datastore/F-D0047-061',
                        '新北市': 'https://opendata.cwa.gov.tw/api/v1/rest/datastore/F-D0047-069',
                        '南投縣': 'https://opendata.cwa.gov.tw/api/v1/rest/datastore/F-D0047-021',
                    }
                    const selectLocation = '南投縣'
                    axios.get(urls[selectLocation], {
                        params: {
                            Authorization: auth,
                            // DataTime: dayjs().format('YYYY-MM-DDTHH:00:00'),
                            timeFrom: dayjs().format('YYYY-MM-DDTHH:00:00'),
                            timeTo: dayjs().add(3, 'hour').format('YYYY-MM-DDTHH:00:01'),
                        }
                    })
                    .then((res) => {
                        // console.log('weather success', res.data.records.Locations[0].Location)
                        const weatherLocations = res.data?.records?.Locations?.[0]?.Location
                        const userLocation = {
                            latitude: latitude,
                            longitude: longitude
                        }
                        const element = (elements: any[], name: string) =>
                            elements?.find((e: any) => e.ElementName === name)?.Time?.[0]?.ElementValue?.[0]

                        type Location = {
                            LocationName: string,
                            Latitude: string,
                            Longitude: string,
                            distance: number,
                            WeatherElement: any,
                        }
                        const nearestTown = weatherLocations?.map((location: Location) => ({
                            // ...location,
                            distance: getDistance(userLocation, {
                                latitude: parseFloat(location.Latitude),
                                longitude: parseFloat(location.Longitude),
                            }),
                            locationName: location.LocationName,
                            temperature: element(location.WeatherElement, '溫度')?.Temperature,
                            apparentTemperature: element(location.WeatherElement, '體感溫度')?.ApparentTemperature,
                            weather: element(location.WeatherElement, '天氣現象')?.Weather,
                            Confort: element(location.WeatherElement, '舒適度指數')?.ComfortIndexDescription,
                        })).sort((a: any, b: any) => a.distance - b.distance)[0]

                        if (nearestTown) {
                            if (nearestTown.distance <= 40000) {
                            set({ weather: {
                                message: 'success', 
                                data: nearestTown
                            }})
                            }
                            else {
                                set({ weather: {
                                    message: `使用者似乎不在${selectLocation}範圍內`,
                                    data: false
                                }})
                            }
                        }
                        else {
                            set({ weather: {
                                message: '找不到天氣資料',
                                data: false
                            }})
                        }
                    })
                    .catch((err) => {
                        console.log('weather fail', err)
                        set({ weather: {
                            message: err,
                            data: false
                        }})
                    })
                }
            }
        }),
        {
            name: 'location',
            partialize: (state) => ({
                location: state.geo,
                weather: state.weather,
            }),
        }
    )
)

export default useLocation
